import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { subscribeN4, when } from '../lib/n4';
import { Decode } from '../lib/fx';
import { SiteActions } from '../components/sites/SiteActions';
import { FileManager } from '../components/sites/FileManager';
import { LogView } from '../components/sites/LogView';
import { SiteSettings } from '../components/sites/SiteSettings';
import { ReleasesList } from '../components/sites/ReleasesList';
import { BackupsList } from '../components/sites/BackupsList';
import { TestInstance } from '../components/sites/TestInstance';
import { EventsTimeline } from '../components/sites/EventsTimeline';
import { KIND_LABEL, LAMP_TEXT, type Site, certClass, daysLeft, errText, flag, healthOf, lampOf, normalizeSite, pickList, pickSite, siteApi, toMs, uptimeOf, useProjects } from '../components/sites/common';

const TABS = [['files', 'Mappa'], ['log', 'Napló'], ['settings', 'Beállítások'], ['releases', 'Kiadások'], ['backups', 'Mentések'], ['test', 'Teszt-példány']] as const;
type Tab = (typeof TABS)[number][0];

// Egy weboldal részlete: fejléc (állapot + vezérlő gombok), fülek, alul az események idővonala.
export function SiteDetail() {
  const { id } = useParams();
  const siteId = Number(id);
  const navigate = useNavigate();
  const projects = useProjects();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'files') as Tab;
  const [site, setSite] = useState<Site | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [missing, setMissing] = useState(false);
  const [actErr, setActErr] = useState('');
  const [tick, setTick] = useState(0);
  const tabsRef = useRef<HTMLElement>(null); const inkRef = useRef<HTMLSpanElement>(null);

  // A részlet-útvonal még hiányozhat a szerverről: ilyenkor a listából keressük ki
  const load = useCallback(() => {
    siteApi<unknown>(siteId).then((r) => { const s = pickSite(r); if (s) { setSite(s); setLoadErr(''); } else throw new Error('üres válasz'); })
      .catch((e: unknown) => {
        api<unknown>('/api/n4/sites').then((r) => {
          const s = pickList<Record<string, unknown>>(r, 'sites').map(normalizeSite).find((x) => x.id === siteId);
          if (s) { setSite(s); setLoadErr(''); } else if (e instanceof ApiError && e.status === 404) setMissing(true); else setLoadErr(errText(e, 'A weboldal'));
        }).catch(() => setLoadErr(errText(e, 'A weboldal')));
      });
    setTick((t) => t + 1);
  }, [siteId]);
  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30_000);
    const off = subscribeN4((ev) => { if (ev.type === 'site' || ev.type === 'edge') load(); });
    return () => { clearInterval(t); off(); };
  }, [load]);
  const onChanged = (fresh: Site | null) => { if (fresh) setSite((s) => ({ ...fresh, live: fresh.live ?? s?.live ?? null })); load(); };

  // Fül-csúszka a kijelölt fül alá
  useLayoutEffect(() => {
    const place = () => { const on = tabsRef.current?.querySelector<HTMLElement>('[data-on]'); const ink = inkRef.current; if (!on || !ink) return; ink.style.left = `${on.offsetLeft}px`; ink.style.width = `${on.offsetWidth}px`; };
    place(); void document.fonts?.ready.then(place); window.addEventListener('resize', place); return () => window.removeEventListener('resize', place);
  });

  if (missing) return <div className="a-pg"><p className="a-sub">Nincs ilyen weboldal (#{siteId}).</p><Link className="a-btn sm" to="/sites">‹ Vissza a weboldalakhoz</Link></div>;
  if (!site) return <div className="a-pg">{loadErr ? <div className="a-site-msg bad a-site-block">{loadErr} <button className="a-btn sm" onClick={load}>Újra</button> <Link className="a-btn sm ghost" to="/sites">‹ Vissza</Link></div> : <p className="a-sub">betöltés…</p>}</div>;

  const lamp = lampOf(site);
  const health = healthOf(site.live);
  const up = site.live?.running ? uptimeOf(site.live) : '';
  const certDays = daysLeft(site.live?.cert_expires);
  const project = site.project_id != null ? projects.find((p) => p.id === site.project_id) : null;
  const lastDeploy = toMs(site.live?.last_deploy);

  return (
    <div className="h-full overflow-auto">
      <div className="a-pg a-site-detail">
        <header className={`a-site-head ${lamp}`}>
          <div className="min-w-0">
            <div className="a-crumbs"><a onClick={() => navigate('/sites')}>‹ Weboldalak</a><span>/</span><span>{site.name}</span></div>
            <h1 className="a-title a-ptitle a-site-h1"><Decode text={site.title || site.name} duration={700} /></h1>
            <div className="a-site-doms big">
              {site.domains.map((d) => <a key={d} href={`https://${d}`} target="_blank" rel="noreferrer" title="Megnyitás új lapon">{d}</a>)}
              {!site.domains.length && <span className="none">nincs domain</span>}
            </div>
            <div className="a-site-meta">
              <span className={`st ${lamp}`}><i className={`a-sitelamp ${lamp}`} />{LAMP_TEXT[lamp]}{up ? ` · ${up} óta` : ''}</span>
              {health.text && <span className={`hl ${health.state}`}>{health.text}</span>}
              {site.live?.error && <span className="hl bad">{site.live.error}</span>}
              {(site.live?.container_status || site.live?.unit_state) && <span>{site.kind === 'container' ? 'konténer' : 'egység'} <b>{site.live.container_status || site.live.unit_state}</b>{site.live.restarts ? ` · ${site.live.restarts} újraindulás` : ''}</span>}
              <span className={`a-site-kind k-${site.kind}`}>{KIND_LABEL[site.kind] ?? site.kind}</span>
              {project ? <Link to={`/projects/${project.id}`}>projekt: <b>{project.name}</b></Link> : site.project_id != null ? <span>projekt: <b>#{site.project_id}</b></span> : <span className="mut">nincs projekt</span>}
              {site.upstream_port != null && <span>port <b>{site.upstream_port}</b></span>}
              {site.unit && <span>egység <b>{site.unit}</b></span>}
              {certDays != null && <span className={`a-site-cert ${certClass(certDays)}`}>cert {certDays < 0 ? 'LEJÁRT' : `${certDays} nap`}</span>}
              <span>kiadás <b>{lastDeploy ? when(lastDeploy) : '—'}</b></span>
            </div>
          </div>
          <div className="a-site-side">
            <SiteActions site={site} onDone={onChanged} onError={setActErr} />
            {site.domains[0] && <a className="a-btn sm ghost a-site-open" href={`https://${site.domains[0]}`} target="_blank" rel="noreferrer">↗ Megnyitás új lapon</a>}
          </div>
        </header>
        {flag(site.maintenance) && <div className="a-site-maintbar">Karbantartás BE — a látogatók a karbantartás-oldalt kapják (503){site.maintenance_text ? `: „${site.maintenance_text}”` : '.'}</div>}
        {actErr && <div className="a-site-err"><span>{actErr}</span><button type="button" onClick={() => setActErr('')} title="Bezárás">×</button></div>}
        {loadErr && <div className="a-site-msg bad">{loadErr}</div>}

        <nav className="a-ptabs" ref={tabsRef}>
          {TABS.map(([key, label]) => (
            <button key={key} data-on={tab === key ? '1' : undefined} onClick={() => setParams({ tab: key }, { replace: true })} className={`a-ptab ${tab === key ? 'on' : ''}`}>{label}</button>
          ))}
          <span className="a-ptab-ink" ref={inkRef} aria-hidden="true" />
        </nav>
        <div className="a-site-tab">
          {tab === 'files' && <FileManager key={site.id} site={site} />}
          {tab === 'log' && <LogView key={site.id} site={site} />}
          {tab === 'settings' && <SiteSettings site={site} projects={projects} onSaved={onChanged} />}
          {tab === 'releases' && <ReleasesList site={site} onChanged={() => load()} />}
          {tab === 'backups' && <BackupsList site={site} onChanged={() => load()} />}
          {tab === 'test' && <TestInstance site={site} onChanged={() => load()} />}
        </div>
        <EventsTimeline siteId={site.id} tick={tick} />
      </div>
    </div>
  );
}
